import React from 'react';
import {
  KatButton, KatModal, KatRadiobuttonGroup, KatAlert,
} from '@amzn/katal-react';
import { useTranslation, customFetch } from 'src/utils';
import { Formik, Form } from 'formik';
import { useAppSelector } from 'src/redux/hooks';
import FormikField from 'src/components/FormikField';
import FetchStatus from 'src/types/FetchStatus';
import { getFlagPostApi, getFlagPostV2Api } from 'src/constants/apiEndpoints';
import { getErrors, commonPostActionErrors } from 'src/redux/slices/threadDetailSlice';
import { FlagPostOptions, PostErrors } from 'src/types/DiscussionPost';
import { DiscussionPostErrors } from 'src/constants/errorStrings';
import { selectIsWeblabIdInT1 } from 'src/redux/slices/weblab';
import WeblabId from 'src/weblab/weblabId';

interface FlagPostModalProps {
  postId: string;
  threadId: string;
  onDismiss: (event: KatModal.CloseEvent | React.MouseEvent<KatButton.Element>) => void;
  onSuccess: () => void;
}

const FlagPostModal: React.FC<FlagPostModalProps> = ({
  postId, onDismiss, onSuccess, threadId,
}) => {
  const { t } = useTranslation();
  const isThreadDetailSSR = useAppSelector(
    (state) => selectIsWeblabIdInT1(state, WeblabId.SELLER_FORUMS_THREAD_DETAILS_SSR),
  );
  return (
    <Formik
      initialValues={{
        flagType: '',
      }}
      onSubmit={async (values, { setStatus }) => {
        const apiPath = isThreadDetailSSR
          ? getFlagPostV2Api(threadId, postId)
          : getFlagPostApi(threadId, postId);
        setStatus(FetchStatus.loading);
        const submitResponse = await customFetch(
          apiPath,
          {
            method: 'PUT',
            body: JSON.stringify(values),
          },
          (error, responseInfo) => {
            const { actionErrors } = getErrors(
              responseInfo as { status: number, data: any },
              commonPostActionErrors,
            );
            if (actionErrors.length > 0) {
              setStatus({ errors: actionErrors });
            } else {
              setStatus({ errors: [PostErrors.flagPost] });
            }
          },
        );
        if (submitResponse) onSuccess();
      }}
    >
      {({
        isSubmitting, values, status,
      }) => (
        <Form>
          <KatModal
            data-testid="flag-post-modal"
            onClose={onDismiss}
            visible
          >
            <div slot="title">
              {t('flagPost_title')}
              <div className="mt-1 whitespace-normal text-sm font-normal">
                {t('flagPost_titleDescription')}
              </div>
            </div>
            <FormikField
              data-testid="flag-options-group"
              component={KatRadiobuttonGroup}
              uniqueId="flagType"
              name="flagType"
              label={t('flagPost_optionsLabel')}
              options={[
                {
                  value: FlagPostOptions.offTopicInappropriate,
                  label: t('flagPost_option_offTopicInappropriate'),
                },
                {
                  value: FlagPostOptions.spamExternalLink,
                  label: t('flagPost_option_spamExternalLink'),
                },
                {
                  value: FlagPostOptions.piiData,
                  label: t('flagPost_option_piiData'),
                },
                {
                  value: FlagPostOptions.duplicateIncorrectCategory,
                  label: t('flagPost_option_duplicateIncorrectCategory'),
                },
                {
                  value: FlagPostOptions.linksOutOfDate,
                  label: t('flagPost_option_linksOutOfDate'),
                },
              ]}
            />
            {
            status?.errors && status.errors.map((error: PostErrors) => (
              <KatAlert
                key={error}
                data-testid="flagpost-failure-alert"
                persistent
                variant="warning"
                header={t(DiscussionPostErrors[error].header)}
                description={t(DiscussionPostErrors[error].description)}
              />
            ))
            }
            <div slot="footer" className="kat-group-horizontal">
              <KatButton data-testid="cancel-flag" onClick={onDismiss} loading={isSubmitting} disabled={isSubmitting} variant="secondary" label={t('cancel')} />
              <KatButton data-testid="submit-flag" loading={isSubmitting} disabled={isSubmitting || !values.flagType} type="submit" label={t('flagPost_submit')} />
            </div>
          </KatModal>
        </Form>
      )}
    </Formik>

  );
};
export default FlagPostModal;
